import { normalizeBrainwaveLab } from "./brainwave-lab.js";

export const BRAINWAVE_PRESET_SCHEMA = "acelab.brainwave-control-board.v1";
export const BRAINWAVE_PRESET_STORAGE_KEY = "acelab_brainwave_presets_v1";

const BUILT_IN_PRESETS = Object.freeze([
  {
    id: "builtin:event-horizon",
    name: "Event Horizon",
    description: "The full headphone spectacle: slow voice helix, endless descent, breathing air vortex and rare cinematic transitions.",
    settings: {
      enabled: true, binauralEnabled: true, toneEnabled: false, subliminalEnabled: true,
      polytemporalEnabled: true, spatialEnabled: true, breathEnabled: true,
      heartbeatEnabled: true, breathSoundEnabled: true,
      echoEnabled: true, filtersEnabled: true, mixEnabled: true,
      spatialSpectacleEnabled: true, spatialSpectacleIntensity: 0.62,
      spatialSpiralEnabled: true, spatialSpiralSeconds: 38,
      spatialSpiralRadius: 0.78, spatialSpiralDescent: 0.72,
      spatialStaircaseEnabled: true, spatialStaircaseLevel: 0.009,
      spatialStaircaseSeconds: 31, spatialVortexEnabled: true,
      spatialVortexLevel: 0.006, spatialBreathRoomDepth: 0.42,
      spatialTrapdoorEnabled: true, spatialTrapdoorLevel: 0.014,
      spatialTrapdoorIntervalSeconds: 240,
      baseFrequencyHz: 128, beatHz: 4.8, modulationDepth: 0.27,
      bassPulseBpm: 36, heartbeatLevel: 0.011, heartbeatCarrierHz: 44,
      breathInSeconds: 3.5, breathOutSeconds: 4, breathSoundLevel: 0.007,
      carrierShiftHz: -420, phaseDegrees: 28, stereoDelayMs: 4.5,
      echoDelayMs: 150, echoFeedback: 0.025, echoMix: 0.035,
      highpassHz: 145, lowpassHz: 3100, crossfeed: 0.06,
      polytemporalLevel: 0.016, polytemporalDepth: 0.38,
      polytemporalSpatialWidth: 0.72, polytemporalTransientLevel: 0.07,
      polytemporalCarrierProfile: "soft", polytemporalMovement: "wide",
      polytemporalContinuousDeepening: true,
      polytemporalDeepeningCycleSeconds: 420, polytemporalDescentBias: 0.92,
    },
  },
  {
    id: "builtin:somatic-tide",
    name: "Somatic Tide",
    description: "Subtle 36 BPM low-bass lub-dub with a long 3.5-in / 4-out air-breath contour and restrained theta field.",
    settings: {
      enabled: true, binauralEnabled: true, toneEnabled: false, subliminalEnabled: true,
      polytemporalEnabled: true, spatialEnabled: true, breathEnabled: true,
      heartbeatEnabled: true, breathSoundEnabled: true,
      echoEnabled: true, filtersEnabled: true, mixEnabled: true,
      baseFrequencyHz: 132, beatHz: 5.5, modulationDepth: 0.22,
      bassPulseBpm: 36, heartbeatLevel: 0.012, heartbeatCarrierHz: 46,
      heartbeatDoubletMs: 165, breathInSeconds: 3.5, breathOutSeconds: 4,
      breathSoundLevel: 0.008, breathAirColor: "soft", breathDepth: 0.18,
      carrierShiftHz: -350, phaseDegrees: 16, stereoDelayMs: 3,
      echoDelayMs: 140, echoFeedback: 0.02, echoMix: 0.03,
      highpassHz: 150, lowpassHz: 3200,
      polytemporalLevel: 0.014, polytemporalDepth: 0.30,
      polytemporalSpatialWidth: 0.42, polytemporalTransientLevel: 0.06,
      polytemporalCarrierProfile: "soft", polytemporalPulseClarity: "soft",
      polytemporalComfort: "balanced", polytemporalMovement: "subtle",
      polytemporalRegularity: "steady", polytemporalContinuousDeepening: true,
      polytemporalDeepeningCycleSeconds: 420, polytemporalDescentBias: 0.86,
    },
  },
  {
    id: "builtin:endless-descent",
    name: "Endless Descent",
    description: "Voice-forward continuous deepening waves with restrained landmarks and dark spatial contraction.",
    settings: {
      enabled: true, binauralEnabled: true, toneEnabled: false, subliminalEnabled: true,
      polytemporalEnabled: true, spatialEnabled: true, breathEnabled: true,
      echoEnabled: true, filtersEnabled: true, mixEnabled: true,
      baseFrequencyHz: 132, beatHz: 5.2, modulationDepth: 0.32,
      carrierShiftHz: -520, phaseDegrees: 24, stereoDelayMs: 4.5,
      echoDelayMs: 150, echoFeedback: 0.025, echoMix: 0.035,
      breathCycleSeconds: 7, breathDepth: 0.18, crossfeed: 0.07,
      highpassHz: 150, lowpassHz: 3000,
      polytemporalLevel: 0.019, polytemporalDepth: 0.43,
      polytemporalSpatialWidth: 0.64, polytemporalTransientLevel: 0.09,
      polytemporalCarrierProfile: "soft", polytemporalPulseClarity: "clear",
      polytemporalComfort: "strong", polytemporalMovement: "subtle",
      polytemporalRegularity: "organic", polytemporalContinuousDeepening: true,
      polytemporalDeepeningCycleSeconds: 360, polytemporalDescentBias: 0.9,
    },
  },
  {
    id: "builtin:velvet-descent",
    name: "Velvet Descent",
    description: "Dark, low-level headphone field with slow spatial motion.",
    settings: {
      enabled: true, binauralEnabled: true, toneEnabled: false, subliminalEnabled: true,
      polytemporalEnabled: true, spatialEnabled: true, breathEnabled: true,
      echoEnabled: true, filtersEnabled: true, mixEnabled: true,
      baseFrequencyHz: 132, beatHz: 4.5, modulationDepth: 0.28,
      carrierShiftHz: -400,
      phaseDegrees: 18, stereoDelayMs: 3.5, echoDelayMs: 180,
      echoFeedback: 0.02, echoMix: 0.03, breathCycleSeconds: 7,
      breathDepth: 0.18, crossfeed: 0.06, highpassHz: 180, lowpassHz: 3200,
      polytemporalLevel: 0.016, polytemporalDepth: 0.32,
      polytemporalSpatialWidth: 0.48, polytemporalTransientLevel: 0.08,
      polytemporalCarrierProfile: "soft",
    },
  },
  {
    id: "builtin:speaker-pulse",
    name: "Speaker Pulse",
    description: "Monaural modulation emphasized for speakers; stereo tricks restrained.",
    settings: {
      enabled: true, binauralEnabled: false, toneEnabled: false, subliminalEnabled: true,
      polytemporalEnabled: true, spatialEnabled: false, breathEnabled: true,
      echoEnabled: false, filtersEnabled: true, mixEnabled: true,
      beatHz: 6, modulationDepth: 0.36, breathCycleSeconds: 6,
      carrierShiftHz: 0,
      breathDepth: 0.2, highpassHz: 120, lowpassHz: 4200,
      polytemporalLevel: 0.021, polytemporalDepth: 0.42,
      polytemporalSpatialWidth: 0, polytemporalTransientLevel: 0.1,
      polytemporalCarrierProfile: "balanced",
    },
  },
  {
    id: "builtin:spatial-orbit",
    name: "Spatial Orbit",
    description: "Headphone-oriented interaural geometry with conservative output.",
    settings: {
      enabled: true, binauralEnabled: true, toneEnabled: false, subliminalEnabled: true,
      polytemporalEnabled: true, spatialEnabled: true, breathEnabled: true,
      echoEnabled: true, filtersEnabled: true, mixEnabled: true,
      baseFrequencyHz: 180, beatHz: 6, modulationDepth: 0.22,
      carrierShiftHz: -250,
      phaseDegrees: 42, stereoDelayMs: 7, echoDelayMs: 150,
      echoFeedback: 0.025, echoMix: 0.035, crossfeed: 0.09,
      polytemporalLevel: 0.014, polytemporalDepth: 0.28,
      polytemporalSpatialWidth: 0.8, polytemporalTransientLevel: 0.07,
      polytemporalMovement: "wide",
    },
  },
  {
    id: "builtin:gentle-calibration",
    name: "Gentle Calibration",
    description: "Low-intensity starting point for comfort and audibility checks.",
    settings: {
      enabled: true, binauralEnabled: true, toneEnabled: true, subliminalEnabled: false,
      polytemporalEnabled: true, spatialEnabled: false, breathEnabled: true,
      echoEnabled: false, filtersEnabled: true, mixEnabled: true,
      baseFrequencyHz: 160, beatHz: 7, modulationDepth: 0.12, volume: 0.035,
      carrierShiftHz: 0,
      polytemporalLevel: 0.008, polytemporalDepth: 0.16,
      polytemporalSpatialWidth: 0, polytemporalTransientLevel: 0.03,
      polytemporalComfort: "sensitive", polytemporalPulseClarity: "soft",
    },
  },
]);

const cleanName = (value) => String(value || "")
  .replace(/[\u0000-\u001f<>]/g, " ")
  .replace(/\s+/g, " ")
  .trim()
  .slice(0, 60);

const presetId = (name) => `custom:${cleanName(name).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;

function readUserPresets(storage = globalThis.localStorage) {
  try {
    const value = JSON.parse(storage?.getItem(BRAINWAVE_PRESET_STORAGE_KEY) || "[]");
    return Array.isArray(value) ? value.filter((entry) => entry?.id && entry?.name && entry?.settings) : [];
  } catch {
    return [];
  }
}

function writeUserPresets(presets, storage = globalThis.localStorage) {
  try { storage?.setItem(BRAINWAVE_PRESET_STORAGE_KEY, JSON.stringify(presets)); } catch {}
}

const normalizedEntry = (entry, builtIn = false) => ({
  id: String(entry.id),
  name: cleanName(entry.name) || "Untitled preset",
  description: String(entry.description || "").slice(0, 180),
  builtIn,
  createdAt: String(entry.createdAt || ""),
  updatedAt: String(entry.updatedAt || ""),
  useCount: Math.max(0, Number(entry.useCount) || 0),
  lastUsedAt: String(entry.lastUsedAt || ""),
  settings: normalizeBrainwaveLab(entry.settings),
});

export function listBrainwavePresets(storage = globalThis.localStorage) {
  const builtIns = BUILT_IN_PRESETS.map((entry) => normalizedEntry(entry, true));
  const custom = readUserPresets(storage).map((entry) => normalizedEntry(entry, false));
  return [...builtIns, ...custom];
}

export function saveBrainwavePreset(name, settings, storage = globalThis.localStorage) {
  const safeName = cleanName(name);
  if (!safeName) throw new Error("Enter a preset name.");
  const id = presetId(safeName);
  if (id === "custom:") throw new Error("Preset name needs letters or numbers.");
  const now = new Date().toISOString();
  const presets = readUserPresets(storage);
  const existing = presets.find((entry) => entry.id === id);
  const entry = normalizedEntry({
    id,
    name: safeName,
    description: existing?.description || "User-authored control-board preset.",
    createdAt: existing?.createdAt || now,
    updatedAt: now,
    useCount: existing?.useCount || 0,
    lastUsedAt: existing?.lastUsedAt || "",
    settings,
  });
  const next = [...presets.filter((item) => item.id !== id), entry]
    .sort((left, right) => left.name.localeCompare(right.name));
  writeUserPresets(next, storage);
  return entry;
}

export function deleteBrainwavePreset(id, storage = globalThis.localStorage) {
  if (!String(id).startsWith("custom:")) return false;
  const presets = readUserPresets(storage);
  const next = presets.filter((entry) => entry.id !== id);
  if (next.length === presets.length) return false;
  writeUserPresets(next, storage);
  return true;
}

export function markBrainwavePresetUsed(id, storage = globalThis.localStorage) {
  if (!String(id).startsWith("custom:")) return;
  const presets = readUserPresets(storage);
  const next = presets.map((entry) => entry.id === id ? {
    ...entry,
    useCount: Math.max(0, Number(entry.useCount) || 0) + 1,
    lastUsedAt: new Date().toISOString(),
  } : entry);
  writeUserPresets(next, storage);
}

export function serializeBrainwavePreset(name, settings) {
  return JSON.stringify({
    schema: BRAINWAVE_PRESET_SCHEMA,
    exportedAt: new Date().toISOString(),
    preset: {
      name: cleanName(name) || "Exported preset",
      settings: normalizeBrainwaveLab(settings),
    },
  }, null, 2);
}

export function parseBrainwavePreset(text) {
  let payload;
  try {
    payload = JSON.parse(String(text || ""));
  } catch {
    throw new Error("Preset file is not valid JSON.");
  }
  if (payload?.schema !== BRAINWAVE_PRESET_SCHEMA || !payload?.preset?.settings) {
    throw new Error("Unsupported control-board preset schema.");
  }
  const name = cleanName(payload.preset.name);
  if (!name) throw new Error("Preset file has no valid name.");
  return { name, settings: normalizeBrainwaveLab(payload.preset.settings) };
}
